<script setup>
import { isNull, isEmpty, uniq } from 'lodash'
import { computed, ref } from 'vue'

import { ORGANIZATION_ENTITY_KEYS } from '@/utils/entity-keys'
import { levelToOkrIconElement } from '@/utils/objectives'
import { stringOrNullProp } from '@/utils/prop-validators'

import AppLogo from '@/components/AppLogo'
import OkrIcon from '@/components/objectives/items/OkrIcon'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppCheckbox from '@/components/ui/AppCheckbox/AppCheckbox'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppSelectListItem from '@/components/ui/AppSelect/AppSelectListItem'
import AppTitle from '@/components/ui/AppTitle/AppTitle'

defineOptions({
  name: 'MCPConnector'
})

const props = defineProps({
  organizations: {
    type: Array,
    required: true
  },

  redirectParams: {
    default: null,
    validator: v => stringOrNullProp(v)
  }
})

const selectedOrganization = ref(null)

const isApproveButtonDisabled = computed(() => {
  if (!accessScope.value) {
    return !selectedOrganization.value
  }

  return !selectedOrganization.value || !isScopeGranted.value
})

const isScopeGranted = ref(false)

const emit = defineEmits({
  confirm: id => !isNull(id)
})

const onConfirm = () => {
  if (!selectedOrganization.value) return

  emit('confirm', selectedOrganization.value)
}

const { ID, NAME } = ORGANIZATION_ENTITY_KEYS

const isNoOrganizations = computed(() => isEmpty(props.organizations))

const pageTitle = computed(() =>
  isNoOrganizations.value ? 'mcp.connector.no_organizations' : 'mcp.connector.title'
)

const accessScope = computed(() => {
  if (!props.redirectParams) return null

  const urlSearchParams = new URLSearchParams(props.redirectParams)

  const query = Object.fromEntries(urlSearchParams.entries())

  return {
    scope: query.scope ? uniq(query.scope.trim().split(/\s+/).filter(Boolean)) : [],
    clientName: query.client_name || 'unknown Client'
  }
})
</script>

<template>
  <div class="mcpc-Wrapper">
    <div class="mcpc-Wrapper_Header">
      <AppLogo height="24" width="127" />
    </div>
    <div class="mcpc-Wrapper_Content">
      <AppIcon height="40" icon-name="mcp-connect-hero" width="120" />
      <AppTitle :level="1" class="mcpc-Title" disable-margin style="--white-space: normal">
        {{ $t(pageTitle) }}
      </AppTitle>

      <div v-if="!isNoOrganizations" class="mcpc-Organizations">
        <div class="mcpc-Organizations_Content">
          <div class="mcpc-OrganizationsList">
            <AppSelectListItem
              v-for="organization in organizations"
              :key="organization[ID]"
              :focused="selectedOrganization === organization[ID]"
              :label="organization[NAME]"
              :model-value="selectedOrganization"
              :selected="selectedOrganization === organization[ID]"
              :val="organization[ID]"
              @select="selectedOrganization = organization[ID]"
            >
              <template #option-label>
                <div class="mcpc-OrganizationItem">
                  <OkrIcon :objective="levelToOkrIconElement(organization)" />
                  <span class="oboard-truncated-text">
                    {{ organization[NAME] }}
                  </span>
                </div>
              </template>
            </AppSelectListItem>
          </div>
        </div>
      </div>

      <template v-if="!isNoOrganizations">
        <div v-if="accessScope" class="mcpc-RequestedAccess">
          <AppTitle :level="3" disable-top-margin style="--white-space: normal">
            {{ $t('mcp.connector.app_request_access', { appName: accessScope.clientName }) }}
          </AppTitle>

          <div v-for="scope in accessScope.scope" :key="scope" class="mcpc-ScopeItem">
            <AppIcon icon-name="info-success" height="24" width="24" class="mcpc-ScopeItem_Icon" />
            <span class="oboard-truncated-text">
              {{ scope }}
            </span>
          </div>

          <AppCheckbox v-model="isScopeGranted" class="mcpc-ScopeCheckbox">
            {{ $t('mcp.connector.grant_access') }}
          </AppCheckbox>
        </div>

        <div class="mcpc-Actions">
          <AppButtonNext :disabled="isApproveButtonDisabled" @click="onConfirm">
            {{ $t('action.approve') }}
          </AppButtonNext>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';

.mcpc-Wrapper {
  height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr;
}

.mcpc-Wrapper_Header {
  height: 80px;
  width: 100%;
  display: flex;
  align-items: center;
  padding-inline: 20px;
}

.mcpc-Title {
  text-align: center;
}

.mcpc-Wrapper_Content {
  max-width: 768px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 32px;
  margin-inline: auto;

  :deep(.asi-AppSelectOptionsItem-selected),
  :deep(.asi-AppSelectOptionsItem-selected.asi-AppSelectOptionsItem-focused) {
    background-color: rgba($primary-color-next, 0.15);
    color: $dark-1;
    @include activityStates(rgba($primary-color-next, 0.15));
  }
}

.mcpc-Organizations {
  min-height: 256px;
}

.mcpc-Organizations_Content {
  overflow: hidden;
  border-radius: $border-radius-sm-next;
  box-shadow: $common-box-shadow;
  background-color: $white;
}
.mcpc-OrganizationsList {
  max-height: 220px;
  min-width: 300px;
  max-width: 620px;
  overflow-y: auto;
}

.mcpc-OrganizationItem {
  color: $dark-1;
  line-height: 20px;
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.mcpc-Actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.mcpc-RequestedAccess {
  max-width: 768px;
}
.mcpc-ScopeItem {
  min-height: 44px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid $grey-2-next;
  gap: 4px;
}

.mcpc-ScopeItem_Icon {
  flex-shrink: 0;
}

.mcpc-ScopeCheckbox {
  margin-top: 16px;
  --checkmark-margin: 0 0 0 4px;
}
</style>
